import { Row, Spacer } from "@amzn/stencil-react-components/layout";
import { Link } from "@amzn/stencil-react-components/link";
import {
  MessageBanner,
  MessageBannerLink,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { trackStructEvent } from "@snowplow/browser-tracker";
import React, { useContext, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useQueryClient } from "react-query";

import { VerificationStatus } from "@/apis/IdentityCheckHelper";
import { ChatWithITSupportFlyoutLink } from "@/components/GetSupport";
import { CHAT_SUPPORT_CONTEXTS_METRICS_MAP } from "@/components/GetSupport/ChatWithITSupport/constants";
import { setLastFailedIdvType } from "@/components/IdentityCheck/helper/rivHelper";
import { useGetWizardEntitlements } from "@/components/IdentityCheck/WizardPathChooser/hooks/useGetWizardEntitlements";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import {
  Day1EventAction,
  Day1EventCategory,
  Day1EventLabel,
} from "@/types/snowplow-events";

// @visibleForTesting
export const IVVFailedReason = [
  "TIMED_OUT",
  "REJECTED",
  "WIDGET_LOAD_FAILED",
  "ABORTED",
] as const;

export type IVVFailedProps = {
  failedReason: (typeof IVVFailedReason)[number];
  allowManualIdvFallbackOverride?: boolean;
};

/**
 * Conditionally render this component which will set properties then mount src/components/IdentityCheck/VerificationCompleted.tsx
 * so that successful IVV shadows the same UX for RIV success.
 */
export const IVVFailed = ({
  failedReason,
  allowManualIdvFallbackOverride,
}: IVVFailedProps): JSX.Element => {
  const { dispatch } = useContext(AppContext);
  const {
    loading: wizardEntitlementsLoading,
    metadata: wizardEntitlementsMetadata,
  } = useGetWizardEntitlements();
  const queryClient = useQueryClient();

  /**
   * Edge case where the API has not completed before rendering this component.
   * Require allowManualIdvFallbackOverride to be a boolean and not autobox undefined
   * as undefined means the API did not deliver a decision when the component rendered.
   */
  const [shouldAllowManualFallback, setShouldAllowManualFallback] =
    useState<boolean>(!!allowManualIdvFallbackOverride);

  /**
   * Set the refPage to provide context to IT Support should the new hire initiate
   * a chat with support
   */
  useEffect(() => {
    // DEV: This is the case where the fall back has been denied __explicitly__. Do not set this to `!shouldAllowManualFallback`
    if (shouldAllowManualFallback === false) {
      dispatch({
        type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
        chatSupportRefPage: "ezo-identity-check-automated-failed-no-fallback",
      });
    }
  }, [shouldAllowManualFallback]);

  useEffect(() => {
    if (
      wizardEntitlementsMetadata &&
      wizardEntitlementsMetadata.isManualIdvEligible !== undefined
    ) {
      setShouldAllowManualFallback(
        wizardEntitlementsMetadata.isManualIdvEligible
      );
    }
  }, [wizardEntitlementsMetadata]);

  useEffect(() => {
    if (failedReason === "TIMED_OUT") {
      // This component is rendered if IVV finished + fails OR if it times out. Track a metric according to the reason it failed.
      trackStructEvent({
        category: Day1EventCategory.AutomatedRivOutcome,
        action: Day1EventAction.SectionToggled,
        label: Day1EventLabel.ResultExpiry,
      });
      // IVVLegalDisclosure component disabled the button earlier, re-enable it now.
    } else {
      // This component is rendered if IVV finished + fails OR if it times out. Track a metric according to the reason it failed.
      trackStructEvent({
        category: Day1EventCategory.AutomatedRivOutcome,
        action: Day1EventAction.SectionToggled,
        label: Day1EventLabel.ResultFail,
      });
    }

    dispatch({
      type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
      nonDirectedChatSupportContext:
        failedReason === "TIMED_OUT" ? "ON_IVV_EXPIRED" : "ON_IVV_REJECTED",
    });
    setLastFailedIdvType("automated");
  }, [queryClient]);

  // We dont know enough about the request yet to show the right options, recheck this.
  if (wizardEntitlementsLoading) {
    return (
      <>
        <Spacer height={"S200"} />
        <Row justifyContent="center">
          <Spinner />
        </Row>
      </>
    );
  }

  const handleFallbackClick = () => {
    // Handle clicking on "please Record Videos" which redirects to manual RIV.
    trackStructEvent({
      category: Day1EventCategory.AutomatedRivOutcome,
      action: Day1EventAction.RecordVideoClicked,
      label:
        failedReason === "TIMED_OUT"
          ? Day1EventLabel.ResultExpiry
          : Day1EventLabel.ResultFail,
    });

    dispatch({
      type: ActionType.SET_WIZARD_PATH,
      wizardPath: "ManualRIV",
    });

    dispatch({
      type: ActionType.SET_DOCS_VERIFICATION_STATUS,
      docsVerificationStatus: VerificationStatus.NOT_STARTED,
    });
  };

  /**
   * In this case the user opted to abort IVV and at some point
   * reloaded the page, we've already reported this metric, but
   * reloading the GetUserProgress endpoint reset the WizardPath
   * to IVV InFlight, we need to flip them back to the manual
   * process.
   */
  if (failedReason === "ABORTED") {
    dispatch({
      type: ActionType.SET_WIZARD_PATH,
      wizardPath: "ManualRIV",
    });

    dispatch({
      type: ActionType.SET_DOCS_VERIFICATION_STATUS,
      docsVerificationStatus: VerificationStatus.NOT_STARTED,
    });
    // Don't bother rendering any components we're about to
    // be re-routed to the manual path by the parent.
    return <></>;
  }

  return (
    <>
      <Spacer height={"S200"} />
      <MessageBanner
        type={MessageBannerType.Warning}
        dataTestId={`IVVFailed-${failedReason}`}
      >
        {shouldAllowManualFallback && (
          <FormattedMessage
            id="identityCheck-ivv-failedBody"
            values={{
              b: (chunk: unknown) => {
                return <b>{chunk}</b>;
              },
              a: (chunk: unknown) => {
                return (
                  <MessageBannerLink
                    onClick={handleFallbackClick}
                    href="#"
                    data-test-id="SetWizardPathLink"
                    className="message-banner-link"
                  >
                    {chunk}
                  </MessageBannerLink>
                );
              },
            }}
          />
        )}
        {/*Case where the fall back has been denied __explicitly__. Do not set this to `!shouldAllowManualFallback` */}
        {shouldAllowManualFallback === false && (
          <FormattedMessage
            id="identityCheck-ivv-failedBodyNoFallback"
            values={{
              b: (chunk: unknown) => {
                return <b>{chunk}</b>;
              },
              chatWithITSupportLink: (
                <ChatWithITSupportFlyoutLink
                  localeMessageId="text-chatWithITSupport"
                  metricProps={
                    CHAT_SUPPORT_CONTEXTS_METRICS_MAP.ON_IVV_REJECTED_NO_FALL_BACK
                  }
                />
              ),
            }}
          />
        )}
      </MessageBanner>
    </>
  );
};
